import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { Auth } from '../auth/auth';
import { ToastService } from '../services/toaster';
import { Logger } from '../services/logger';

const PUBLIC_ROUTES = [
  '/login',
  '/reset-password',
  '/otp',
  '/verifyotp',
  '/ga-setup',
  '/ga-verify',
  '/ga-otp',
  '/changepassword',
  '/approval/public',
];
// const PUBLIC_ROUTES = ['/reset-password', '/otp', '/ga-setup', '/ga-verify', '/ga-otp', '/approval/public'];

// A response whose message matches one of these means the session/account/
// company is no longer valid - the user must be logged out and sent to
// /login, NOT shown /access-denied. It can arrive as a 401 (authenticate.js
// "Session revoked" / "Unauthorized User" / "jwt expired") OR as a 403 from a
// downstream guard (e.g. verifyCompanyAccess) when the auth check failed open
// on a transient Valkey error and the dead session reached a company/tenant
// guard instead (Bugs 8256 / 8689).
const SESSION_ENDED_PATTERN =
  /session revoked|session (has )?expired|token expired|jwt expired|unauthorized user|account (has been )?deactivat|company (has been )?deactivat|account is inactive|company is inactive|your (account|company) has been/i;

// Bug 8724: a single page load (e.g. /dashboard) fires several parallel GETs
// - when the current company/group has no menu access to any of them, every
// one of those requests 403s independently, and each would otherwise raise
// its own identical "Access Denied" toast. Module-scoped (not per-call) so it
// tracks across the whole burst: suppress a repeat of the exact same message
// within this short window instead of stacking N identical toasts for what
// is, from the user's perspective, a single failure.
const DUPLICATE_TOAST_WINDOW_MS = 3000;
let lastAccessDeniedMessage = '';
let lastAccessDeniedAt = 0;

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(Auth);
  const router = inject(Router);
  const toast = inject(ToastService);
  const logger = inject(Logger);

  const isPublicRoute = () => PUBLIC_ROUTES.some((route) => router.url.startsWith(route));

  const forceLogout = (message: string) => {
    if (isPublicRoute()) return;
    auth.clearSession(message);
    router.navigateByUrl('/login');
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const serverMessage: string = error.error?.message || '';
      const sessionEnded = SESSION_ENDED_PATTERN.test(serverMessage);

      if (error.status === 401 || error.status === 403) {
        // The session is already being torn down (FORCE_LOGOUT socket event,
        // a prior session-ended response, or a manual logout). Every
        // remaining in-flight 401/403 must funnel to /login and MUST NOT
        // navigate to /access-denied - otherwise a late verifyCompanyAccess
        // "Access denied" 403 races the FORCE_LOGOUT redirect and wins,
        // stranding the user on /access-denied with cleared menus (Bug 8689).
        if (auth.sessionTerminated) {
          if (!isPublicRoute()) router.navigateByUrl('/login');
          return throwError(() => error);
        }

        // No authenticated user at all -> the session is gone; a 401 or even
        // a 403 here can only mean "log back in", never "you can't see this
        // page".
        if (!auth.currentUser()) {
          forceLogout(serverMessage || 'Your session has ended. Please log in again.');
          return throwError(() => error);
        }
      }

      if (error.status === 401) {
        // '/' used to be in PUBLIC_ROUTES - since every route string starts
        // with '/', that made isPublicRoute true for every URL in the app,
        // so this redirect never fired for ANY 401 (e.g. Bug 8256: an
        // inactivated admin's session got a 401 "Session revoked" on every
        // request but the app just kept rendering empty data, never
        // bouncing to /login).
        forceLogout(serverMessage || 'Your session has ended. Please log in again.');
      } else if (error.status === 403) {
        if (sessionEnded) {
          // A dead session that surfaced as a 403 (see SESSION_ENDED_PATTERN)
          // - log out, don't show /access-denied (Bug 8689: company
          // inactivation revokes the session but the failing request came
          // back 403, leaving the user stranded on the access-denied page).
          forceLogout(serverMessage || 'Your session has ended. Please log in again.');
        } else if (req.url.includes('/my-policies') || req.url.includes('/my-sidebar')) {
          // Fired from the policy-acknowledgement route guard on every
          // navigation; the gate service handles this 403 itself. Toasting
          // and redirecting to /access-denied here (itself a guarded route)
          // re-triggers the guard and loops.
        } else {
          const message = serverMessage || 'Access Denied';
          const now = Date.now();
          const isDuplicateBurst =
            message === lastAccessDeniedMessage && now - lastAccessDeniedAt < DUPLICATE_TOAST_WINDOW_MS;
          if (!isDuplicateBurst) {
            toast.error(message);
          }
          lastAccessDeniedMessage = message;
          lastAccessDeniedAt = now;
          // Only a page-data GET that slipped past the route guards warrants
          // the full-page Access Denied. A write (POST/PUT/PATCH/DELETE)
          // returning 403 is an action-level business-rule denial (e.g.
          // "Client Admins cannot create Code Group Headers", Bug 11724) -
          // toast and stay on the page instead of tearing the whole app over
          // to /access-denied. Page-level access is already enforced before
          // load by the route guards (permissionGuard, adminRoleGuard,
          // benefit-type-*-guard), which own that redirect.
          if (req.method === 'GET') {
            router.navigateByUrl('/access-denied', { skipLocationChange: true });
          }
        }
      } else {
        logger.error(`HTTP ${error.status} on ${req.method} ${req.url}`, error.error);
      }
      return throwError(() => error);
    }),
  );
};
